import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useCancelRegistration,
  useFlag,
  usePass,
  usePassVouchers,
  useRegister,
  useUseEntryVoucher,
  useRegistrations,
  useTournament,
  useTournamentLive,
} from '../api/hooks';
import { QuestsSheet } from './PassScreen';
import { Loader, ErrorView } from '../components/StateViews';
import { Avatar } from '../components/Avatar';
import { BackIcon } from '../components/icons';
import { backButton, haptics } from '../telegram';
import { SheetClose } from '../components/SheetClose';
import { track } from '../analytics';
import { compact, money, shortDate, timeLabel, weekday } from '../format';

const DIFF_LABEL: Record<string, string> = {
  easy: 'Лёгкое',
  medium: 'Среднее',
  hard: 'Сложное',
};

/** MM:SS, от часа — H:MM:SS (как на Live-экране). */
function fmtCountdown(sec: number): string {
  const s = Math.max(0, sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const mmss = `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  return h > 0 ? `${h}:${mmss}` : mmss;
}

export function TournamentDetailScreen() {
  const { id } = useParams();
  const tid = Number(id);
  const navigate = useNavigate();
  const { data: t, isLoading, isError, error } = useTournament(tid);
  const regs = useRegistrations(tid);
  const register = useRegister(tid);
  const passOn = useFlag('pass');
  const vouchers = usePassVouchers(passOn);
  const useEntry = useUseEntryVoucher();
  const hasEntryVoucher = (vouchers.data ?? []).some(
    (v) => v.type === 'entry' && v.status === 'active',
  );
  const cancel = useCancelRegistration(tid);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Шторка «Задания» (как на экране пасса) — по тапу на карточку задания
  const [questsOpen, setQuestsOpen] = useState(false);
  const pass = usePass(passOn && questsOpen);
  // Live-данные Korin нужны только для идущего турнира (гейт поздней регистрации).
  const liveQ = useTournamentLive(t?.status === 'in_progress' ? tid : NaN);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const tmr = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(tmr);
  }, []);

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  useEffect(() => {
    if (Number.isFinite(tid)) track('tournament_view', { gameId: tid });
  }, [tid]);

  if (isLoading) return <Loader />;
  if (isError || !t) return <ErrorView message={(error as Error)?.message} />;

  const isLive = t.status === 'in_progress';
  // Поздняя регистрация по live-апдейту Korin: между снапшотами дедлайн тикает
  // локально. Если live-данных нет — доверяем серверному registrationOpen
  // (он уже наложил lateRegOpen для идущих игр).
  const liveData = liveQ.data;
  const lateRegRemaining = (() => {
    if (!liveData?.live || !liveData.lateRegOpen) return 0;
    if (liveData.isPaused || !liveData.lateRegEndsAt) return liveData.lateRegTimeLeftSec;
    return Math.max(0, Math.round((Date.parse(liveData.lateRegEndsAt) - nowMs) / 1000));
  })();
  const lateRegStillOpen = !!liveData?.live && liveData.lateRegOpen && lateRegRemaining > 0;
  const lateRegKnownClosed = !!liveData?.live && !lateRegStillOpen;
  const open =
    t.registrationOpen &&
    (isLive ? !lateRegKnownClosed : new Date(t.startTime).getTime() > Date.now());
  const hasCap = t.capacity !== null && t.capacity > 0;
  const pct = hasCap
    ? Math.min(100, Math.round((t.registeredCount / (t.capacity as number)) * 100))
    : 0;
  const seatsLeft = hasCap ? Math.max(0, (t.capacity as number) - t.registeredCount) : 0;
  // Мест нет — запись продолжается в лист ожидания.
  const full = hasCap && seatsLeft === 0;
  const shown = regs.data?.players.slice(0, 5) ?? [];
  const extra = (regs.data?.total ?? t.registeredCount) - shown.length;
  const cancelled = t.status === 'cancelled';
  const past = new Date(t.startTime).getTime() < Date.now();

  const confirm = async (spendVoucher = false) => {
    try {
      await register.mutateAsync();
      // Ваучер «Бесплатный вход» — только после явного выбора в шторке.
      if (spendVoucher) void useEntry.mutateAsync().catch(() => undefined);
      haptics.success();
      setSheetOpen(false);
    } catch {
      haptics.error();
    }
  };

  const doCancel = async () => {
    try {
      await cancel.mutateAsync();
      haptics.success();
    } catch {
      haptics.error();
    }
  };

  return (
    <div>
      <div data-theme="dark" className="detail-hero">
        <img className="hero-mark" src="/logo.png" alt="" aria-hidden="true" />
        <div className="hero-top">
          <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Назад">
            <BackIcon />
          </button>
          <span className={`badge ${open ? 'badge--open' : 'badge--closed'}`}>
            {cancelled ? 'Турнир отменён' : open ? 'Регистрация открыта' : 'Регистрация закрыта'}
          </span>
        </div>
        <div className="hero-title">{t.title ?? t.format ?? 'Турнир'}</div>
        <div className="hero-meta">
          {weekday(t.startTime)} · {shortDate(t.startTime)} · {timeLabel(t.startTime)}
          {t.format ? ` · ${t.format}` : ''}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-card">
          <div className="v">{t.buyIn > 0 ? money(t.buyIn) : 'Free'}</div>
          <div className="l">Бай-ин</div>
        </div>
        <div className="detail-card">
          <div className="v gold">{t.guarantee ? `${compact(t.guarantee)} ₽` : '—'}</div>
          <div className="l">Гарантия</div>
        </div>
        {t.startingStack !== null ? (
          <div className="detail-card">
            <div className="v">{compact(t.startingStack)}</div>
            <div className="l">Стартовый стек</div>
          </div>
        ) : (
          <div className="detail-card">
            <div className="v" style={{ fontFamily: 'var(--font-ui)', fontSize: 14 }}>
              {t.format ?? '—'}
            </div>
            <div className="l">Формат</div>
          </div>
        )}
        {t.blindMinutes !== null ? (
          <div className="detail-card">
            <div className="v">{t.blindMinutes} мин</div>
            <div className="l">Уровни блайндов</div>
          </div>
        ) : (
          <div className="detail-card">
            <div className="v">
              {shortDate(t.startTime)} · {timeLabel(t.startTime)}
            </div>
            <div className="l">Дата</div>
          </div>
        )}
      </div>

      {t.description && (
        <div className="about">
          <div className="about-label">О турнире</div>
          <p>{t.description}</p>
        </div>
      )}

      {t.registeredCount > 0 ? (
        <Link to={`/tournaments/${t.id}/players`} className="card seats-card" style={{ marginTop: 14 }}>
          <div className="reg-head">
            <span className="t">{hasCap ? 'Свободные места' : 'Записались'}</span>
            <span className="c">
              {hasCap ? `${t.registeredCount} / ${t.capacity}` : t.registeredCount}
            </span>
          </div>
          {hasCap && (
            <div className="progress progress--lg" style={{ margin: '14px 0' }}>
              <div className="progress-fill" style={{ width: `${pct}%`, background: 'var(--green)' }} />
            </div>
          )}
          {shown.length > 0 && (
            <div className="avatars" style={{ marginTop: hasCap ? 0 : 14 }}>
              {shown.map((p, i) => (
                <Avatar key={p.playerId} name={p.nickname} src={p.photoUrl} size={28} variant={i} />
              ))}
              {extra > 0 && <span className="more">+{extra} уже записались</span>}
            </div>
          )}
          {hasCap && seatsLeft > 0 && (
            <div className="tiny muted" style={{ marginTop: 12 }}>
              Осталось {seatsLeft} мест
            </div>
          )}
          {t.waitlistCount > 0 && (
            <div className="tiny muted" style={{ marginTop: seatsLeft > 0 ? 4 : 12 }}>
              Лист ожидания: {t.waitlistCount}
            </div>
          )}
          <span className="seats-link seats-link--chev">Все записавшиеся →</span>
        </Link>
      ) : (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="reg-head">
            <span className="t">{hasCap ? 'Свободные места' : 'Записались'}</span>
            <span className="c">
              {hasCap ? `${t.registeredCount} / ${t.capacity}` : t.registeredCount}
            </span>
          </div>
          {hasCap && (
            <div className="progress progress--lg" style={{ margin: '14px 0' }}>
              <div className="progress-fill" style={{ width: `${pct}%`, background: 'var(--green)' }} />
            </div>
          )}
          {hasCap && seatsLeft > 0 && (
            <div className="tiny muted" style={{ marginTop: 12 }}>
              Осталось {seatsLeft} мест
            </div>
          )}
        </div>
      )}

      {isLive && (
        <>
          <Link
            to={`/tournaments/${t.id}/live`}
            className="btn"
            style={{ display: 'block', textAlign: 'center', marginTop: 14 }}
            onClick={() => track('live_click', { gameId: t.id, from: 'detail' })}
          >
            ● Смотреть Live
          </Link>
          {lateRegStillOpen && (
            <div className="tiny muted" style={{ textAlign: 'center', marginTop: 8 }}>
              Поздняя регистрация: осталось {fmtCountdown(lateRegRemaining)}
            </div>
          )}
        </>
      )}
      {past && (
        <Link
          to={`/tournaments/${t.id}/results`}
          className="btn btn--ghost"
          style={{ display: 'block', textAlign: 'center', marginTop: 10 }}
        >
          Результаты турнира →
        </Link>
      )}

      {/* Straddle Pass: моё задание на эту игру — видно с момента записи */}
      {t.registeredByMe && t.myPassQuest && t.myPassQuest.status === 'assigned' && (
        <button
          className="bq-game-card"
          style={{ margin: '14px 0 0', width: '100%', textAlign: 'left', cursor: 'pointer' }}
          onClick={() => {
            haptics.tap();
            track('pass_quests_open', { from: 'tournament' });
            setQuestsOpen(true);
          }}
        >
          <span className="bq-overline">ЗАДАНИЕ НА ИГРУ</span>
          <span className="bq-line">
            <b>{t.myPassQuest.title}</b>
            {t.myPassQuest.difficulty && (
              <span className={`bq-diff bq-diff--${t.myPassQuest.difficulty}`}>
                {DIFF_LABEL[t.myPassQuest.difficulty]}
              </span>
            )}
            <span className="bq-xp mono">+{t.myPassQuest.reward} XP</span>
          </span>
          <span className="bq-sub">Проверим по результатам · заменить нельзя</span>
        </button>
      )}
      {questsOpen && pass.data?.quests && (
        <QuestsSheet quests={pass.data.quests} onClose={() => setQuestsOpen(false)} />
      )}

      <div style={{ height: 96 }} />

      <div className="detail-footer">
        <div className="fbuyin">
          <div className="v">{t.buyIn > 0 ? money(t.buyIn) : 'Free'}</div>
          <div className="l">Бай-ин</div>
        </div>
        {cancelled ? (
          <button className="btn" disabled>
            Турнир отменён
          </button>
        ) : t.registeredByMe ? (
          isLive && lateRegKnownClosed ? (
            // Поздняя регистрация закончилась — отмена уже невозможна (сервер
            // вернул бы «Too late to cancel»), кнопку не показываем.
            <button className="btn" disabled>
              Вы записаны
            </button>
          ) : (
          <div style={{ flex: 1, minWidth: 0 }}>
            {t.myWaitlisted && (
              <div className="tiny muted" style={{ marginBottom: 6, textAlign: 'center' }}>
                Вы в листе ожидания{t.myWaitlistPosition ? ` (№${t.myWaitlistPosition})` : ''}
              </div>
            )}
            <button
              className="btn btn--danger"
              style={{ width: '100%' }}
              onClick={doCancel}
              disabled={cancel.isPending}
            >
              {cancel.isPending
                ? 'Отмена…'
                : t.myWaitlisted
                  ? 'Выйти из листа ожидания'
                  : 'Отменить запись'}
            </button>
          </div>
          )
        ) : (
          <button
            className="btn"
            disabled={!open}
            onClick={() => {
              haptics.tap();
              track('register_click', { gameId: tid });
              setSheetOpen(true);
            }}
          >
            {!open ? 'Регистрация закрыта' : full ? 'В лист ожидания' : 'Записаться'}
          </button>
        )}
      </div>

      {sheetOpen && (
        <div className="sheet-backdrop" onClick={() => setSheetOpen(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <SheetClose onClose={() => setSheetOpen(false)} />
            <h3>{full ? 'Запись в лист ожидания' : 'Запись на турнир'}</h3>
            {full && (
              <div className="tiny muted" style={{ marginBottom: 8 }}>
                Все места заняты. Вы встанете в лист ожидания — как только место
                освободится, вы автоматически попадёте в состав и получите уведомление.
              </div>
            )}
            <div className="sheet-sub">
              {t.title ?? t.format ?? 'Турнир'} · {shortDate(t.startTime)} {timeLabel(t.startTime)}
            </div>
            {t.format && (
              <div className="sheet-row">
                <span className="l">Формат</span>
                <span className="v">{t.format}</span>
              </div>
            )}
            {t.startingStack !== null && (
              <div className="sheet-row">
                <span className="l">Стартовый стек</span>
                <span className="v">{compact(t.startingStack)}</span>
              </div>
            )}
            <div className="sheet-row">
              <span className="l">Бай-ин</span>
              <span className="v">{t.buyIn > 0 ? money(t.buyIn) : 'Free'}</span>
            </div>
            <div className="sheet-row">
              <span className="l">Начало</span>
              <span className="v" style={{ color: 'var(--gold)' }}>
                {shortDate(t.startTime)} · {timeLabel(t.startTime)}
              </span>
            </div>
            {register.isError && (
              <div className="tiny" style={{ color: 'var(--red)', marginTop: 10 }}>
                {(register.error as Error)?.message}
              </div>
            )}
            {hasEntryVoucher ? (
              <>
                <div className="bp-voucher-ask">
                  <b>Ваучер «Бесплатный вход»</b>
                  <span>Потратить ваучер на эту игру? Вход будет бесплатным.</span>
                </div>
                <button
                  className="btn"
                  style={{ marginTop: 12 }}
                  onClick={() => void confirm(true)}
                  disabled={register.isPending}
                >
                  {register.isPending ? 'Записываем…' : 'Записаться с ваучером'}
                </button>
                <button
                  className="btn-secondary bp-btn-secondary"
                  style={{ width: '100%', marginTop: 8 }}
                  onClick={() => void confirm(false)}
                  disabled={register.isPending}
                >
                  Записаться без ваучера
                </button>
              </>
            ) : (
              <button
                className="btn"
                style={{ marginTop: 16 }}
                onClick={() => void confirm(false)}
                disabled={register.isPending}
              >
                {register.isPending ? 'Записываем…' : 'Подтвердить запись'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
